// src/components/layout/MobileNav.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Target,
  BookOpen,
  Sparkles,
  MessageSquare,
  User,
  LogOut,
  Settings,
  Award,
  LogIn,
  Feather,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useDialogA11y from '../../hooks/useDialogA11y';

const MobileNav = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut, redirectToAuth } = useAuth();
  // The menu is the primary navigation on small screens: it must trap Tab,
  // close on Escape, and hand focus back to the hamburger when it closes.
  const dialogRef = useDialogA11y(true, onClose);

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Assessment', href: '/assessment', icon: Target },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Toolkit', href: '/toolkit', icon: Sparkles },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
    { name: 'Resources', href: '/resources', icon: MessageSquare },
    { name: 'The Lineage', href: '/lineage', icon: Sparkles },
    { name: 'Journal', href: '/journal', icon: Feather },
  ];

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out Menu */}
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 20 }}
        className="fixed right-0 top-0 bottom-0 w-80 bg-gunmetal shadow-2xl z-50 lg:hidden overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-end mb-2">
            <button
              onClick={onClose}
              aria-label="Close menu"
              className="p-2 -m-2 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Info */}
          {user && (
            <div className="mb-6 p-4 bg-gold/20 rounded-lg">
              <p className="text-pearl font-medium">{user.displayName || 'User'}</p>
              <p className="text-sm text-muted-gold">{user.email}</p>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="space-y-2 mb-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-gold/20 text-bright-gold' 
                      : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/20'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Actions */}
          {user ? (
            <div className="space-y-2 pt-6 border-t border-charcoal">
              <Link
                to="/profile"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all"
              >
                <User className="w-5 h-5" />
                <span>Profile</span>
              </Link>
              
              <Link
                to="/privacy"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all"
              >
                <Settings className="w-5 h-5" />
                <span>Data & Privacy</span>
              </Link>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all text-left"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Out</span>
              </button>
            </div>
          ) : (
            <div className="pt-6 border-t border-charcoal">
              <button
                onClick={() => { onClose(); redirectToAuth('signin'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gold/20 text-bright-gold hover:bg-gold/30 transition-all font-medium text-left"
              >
                <LogIn className="w-5 h-5" />
                <span>Log In</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default MobileNav;